import {
  CreateCollectionDialog,
  type CreateCollectionSubmission,
} from "components/dialog/CreateCollectionDialog";
import type { CreateCollectionOutcome } from "hooks/use-collection-bookmark";
import { useCollectionClient } from "providers/CollectionClientContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getHttpStatus, type ProjectOption } from "service/collections";

export interface CreateCollectionFlowProps {
  /** Close icon, backdrop, Escape or Cancel. Nothing is created. */
  onClose: () => void;
  /** Creates the collection with the material in it; rejects only if the create itself failed. */
  create: (
    submission: CreateCollectionSubmission,
  ) => Promise<CreateCollectionOutcome>;
  /** The collection exists; the host reports how the save and upload went. */
  onCreated: (outcome: CreateCollectionOutcome) => void;
}

/**
 * The connected Create New Collection dialog for the heart popover. Mount it
 * only while it is wanted: that is when the Projects load, and unmounting
 * resets the form.
 *
 * A 404 with projects chosen means one of them is missing or not writable (the
 * service never says which), so it lands on the project field; anything else
 * is an alert. Either way nothing is created and the form keeps its values.
 */
export const CreateCollectionFlow = ({
  onClose,
  create,
  onCreated,
}: CreateCollectionFlowProps) => {
  const { t } = useTranslation();
  const client = useCollectionClient();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsFailed, setProjectsFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    client.listProjectOptions().then(
      (items) => {
        if (active) {
          setProjects(items);
          setProjectsLoading(false);
        }
      },
      () => {
        if (active) {
          setProjectsFailed(true);
          setProjectsLoading(false);
        }
      },
    );
    return () => {
      active = false;
    };
  }, [client]);

  const handleSubmit = async (submission: CreateCollectionSubmission) => {
    setSubmitError(null);
    setProjectError(null);
    setSubmitting(true);
    let outcome: CreateCollectionOutcome;
    try {
      outcome = await create(submission);
    } catch (error) {
      if (
        getHttpStatus(error) === 404 &&
        submission.body.linkedProjectIds?.length
      ) {
        setProjectError(t("src_collection_dialog_project_refused"));
      } else {
        setSubmitError(t("src_collection_create_error"));
      }
      setSubmitting(false);
      return;
    }
    // Outside the try: the collection exists, so a host-side throw must not
    // read as a failed create (a retry would duplicate it).
    onCreated(outcome);
  };

  return (
    <CreateCollectionDialog
      open
      onClose={onClose}
      onCancel={onClose}
      onSubmit={(submission) => void handleSubmit(submission)}
      projects={projects}
      projectsLoading={projectsLoading}
      projectsFailed={projectsFailed}
      submitting={submitting}
      submitError={submitError}
      projectError={projectError}
    />
  );
};
